import React, { useEffect, useRef, useState } from "react";
import { Box, IconButton } from "@mui/material";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import CircularProgress from "@mui/material/CircularProgress";
import { COMPOSER_PLACEHOLDERS, useStudioTheme } from "../constants";

const Composer = ({
  value,
  onChange,
  onSubmit,
  loading = false,
  autoFocus = false,
  variant = "hero",
  placeholder,
}) => {
  const { palette, shadow, fontStack } = useStudioTheme();
  const textareaRef = useRef(null);
  const [phIndex, setPhIndex] = useState(0);
  const isDocked = variant === "docked";

  useEffect(() => {
    if (placeholder || variant !== "hero" || value) return;
    const t = setInterval(
      () => setPhIndex((i) => (i + 1) % COMPOSER_PLACEHOLDERS.length),
      3200
    );
    return () => clearInterval(t);
  }, [placeholder, variant, value]);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [value]);

  useEffect(() => {
    if (autoFocus) textareaRef.current?.focus();
  }, [autoFocus]);

  const canSend = value.trim().length > 0 && !loading;

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (canSend) onSubmit();
    }
  };

  const resolvedPlaceholder =
    placeholder ||
    (variant === "hero"
      ? COMPOSER_PLACEHOLDERS[phIndex]
      : "Describe a change to the policy…");

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-end",
        gap: 1,
        p: isDocked ? 1 : 1.5,
        backgroundColor: palette.surface,
        border: `1px solid ${palette.border}`,
        borderRadius: isDocked ? "12px" : "16px",
        boxShadow: isDocked ? shadow.sm : shadow.md,
        transition: "border-color 0.2s ease, box-shadow 0.2s ease",
        "&:focus-within": {
          borderColor: palette.brandSoft,
          boxShadow: `0 0 0 3px ${palette.brandTint}`,
        },
      }}
    >
      {!isDocked && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            px: 1,
            py: 0.35,
            borderRadius: "999px",
            backgroundColor: palette.brandTint,
            color: palette.brandDeep,
            fontFamily: fontStack,
            fontSize: "11px",
            fontWeight: 600,
            flexShrink: 0,
            mb: 0.5,
          }}
        >
          <TipsAndUpdatesRoundedIcon sx={{ fontSize: 13 }} />
          Rego AI
        </Box>
      )}

      <Box
        component="textarea"
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        rows={isDocked ? 1 : 2}
        placeholder={resolvedPlaceholder}
        sx={{
          flex: 1,
          resize: "none",
          border: "none",
          outline: "none",
          background: "transparent",
          fontFamily: fontStack,
          fontSize: "14px",
          lineHeight: 1.55,
          color: palette.ink,
          py: 0.5,
          px: 0.5,
          maxHeight: "160px",
          minHeight: "24px",
          "&::placeholder": { color: palette.inkFaint },
        }}
      />

      <IconButton
        onClick={() => canSend && onSubmit()}
        disabled={!canSend}
        size="small"
        sx={{
          width: 34,
          height: 34,
          flexShrink: 0,
          borderRadius: "10px",
          color: palette.buttonText,
          bgcolor: canSend ? palette.buttonPrimary : palette.borderStrong,
          transition: "all 0.15s ease",
          "&:hover": {
            bgcolor: canSend ? palette.buttonHover : palette.borderStrong,
          },
          "&.Mui-disabled": { color: palette.buttonText, opacity: 0.7 },
        }}
      >
        {loading ? (
          <CircularProgress size={16} sx={{ color: palette.buttonText }} />
        ) : (
          <ArrowUpwardRoundedIcon sx={{ fontSize: 18 }} />
        )}
      </IconButton>
    </Box>
  );
};

export default Composer;
